import { useEffect, useMemo, useState } from "react";
import { Wallet, ArrowDown, Loader2, CheckCircle2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { getCoinInfo, getPresaleStats, postPresaleSignup } from "@/lib/api";
import { shortAddress } from "@/lib/wallet";
import { useArcWallet, safeWalletClick } from "@/context/ArcEcosystemContext";

const RATES = { ETH: 3284.5, BNB: 612.3, USDT: 1, USDC: 1 };

export default function PresaleWidget() {
  const { wallet, connect } = useArcWallet();
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [pay, setPay] = useState("0.5");
  const [token, setToken] = useState("ETH");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    Promise.all([getCoinInfo(), getPresaleStats()])
      .then(([i, s]) => { setInfo(i); setStats(s); })
      .catch(() => {});
  }, []);

  const arcAmount = useMemo(() => {
    const usd = (parseFloat(pay) || 0) * (RATES[token] || 0);
    if (!info) return 0;
    return usd / info.presale_price_usd;
  }, [pay, token, info]);

  const submit = async () => {
    if (!wallet?.address) {
      // safeWalletClick guarantees no unhandled rejection escapes.
      await safeWalletClick(connect);
      return;
    }
    if (!pay || parseFloat(pay) <= 0) {
      toast.error("Enter an amount greater than 0");
      return;
    }
    setSubmitting(true);
    try {
      await postPresaleSignup({
        wallet_address: wallet.address,
        email: email || undefined,
        eth_amount: parseFloat(pay),
        arc_amount: arcAmount,
        network: token,
      });
      setDone(true);
      toast.success("Presale allocation reserved! Check your email for next steps.");
      const s = await getPresaleStats();
      setStats(s);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Submission failed";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = stats?.progress_pct ?? 0;
  const raised = stats?.raised_usd ?? 0;
  const target = stats?.target_usd ?? 1500000;

  return (
    <section id="presale" className="relative py-24 sm:py-32" data-testid="presale-section">
      <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">// round 2 · live</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black mt-2">
            Stake your spot in the <span className="shine-text">arena</span>.
          </h2>
          <p className="mt-5 text-white/70 font-mono text-sm sm:text-base leading-relaxed">
            Round 2 is open at <span className="text-[#39FF14]">$0.018 / ARC</span> — listing is
            locked at <span className="text-[#00F0FF]">$0.025 / ARC</span>. Tokens are unlocked
            and claimable 24h after presale closes.
          </p>

          <div className="mt-8 glass p-6">
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Raised</div>
                <div className="font-display text-2xl font-bold text-[#39FF14] glow-green" data-testid="presale-raised">
                  ${raised.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Target</div>
                <div className="font-display text-2xl font-bold text-white">
                  ${target.toLocaleString()}
                </div>
              </div>
            </div>
            <Progress
              value={progress}
              className="h-3 bg-white/5 [&>div]:bg-gradient-to-r [&>div]:from-[#39FF14] [&>div]:to-[#00F0FF]"
              data-testid="presale-progress"
            />
            <div className="mt-3 flex justify-between font-mono text-xs text-white/60">
              <span>{progress.toFixed(1)}% filled</span>
              <span>{stats?.contributors?.toLocaleString?.() || "—"} contributors</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7" id="presale-widget">
          <div className="glass p-6 sm:p-8 relative overflow-hidden" data-testid="presale-widget">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-lg font-bold tracking-wider">BUY ARC</h3>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                {wallet?.address ? `Wallet · ${shortAddress(wallet.address)}` : "Wallet not connected"}
              </span>
            </div>

            {/* PAY */}
            <div className="border border-white/10 p-4 bg-black/40">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">You pay</span>
                <span className="font-mono text-[10px] text-white/40">≈ ${((parseFloat(pay) || 0) * (RATES[token] || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pay}
                  onChange={(e) => setPay(e.target.value)}
                  className="bg-transparent border-0 font-display text-3xl font-bold p-0 h-auto focus-visible:ring-0 text-white"
                  data-testid="presale-pay-input"
                />
                <Select value={token} onValueChange={setToken}>
                  <SelectTrigger
                    className="w-[120px] bg-white/5 border border-white/15 font-display font-bold text-white"
                    data-testid="presale-token-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0b10] border-white/15 text-white">
                    {Object.keys(RATES).map((t) => (
                      <SelectItem key={t} value={t} data-testid={`presale-token-option-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-center my-3">
              <div className="w-9 h-9 rounded-full grid place-items-center bg-[#39FF14] text-black">
                <ArrowDown className="w-4 h-4" />
              </div>
            </div>

            {/* RECEIVE */}
            <div className="border border-[#39FF14]/40 p-4 bg-[#39FF14]/[0.04]">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">You receive</span>
                <span className="font-mono text-[10px] text-white/40">@ ${info?.presale_price_usd?.toFixed(3) || "--"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-display text-3xl font-bold text-[#39FF14] glow-green" data-testid="presale-receive-amount">
                  {arcAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
                <span className="font-display font-bold tracking-widest text-[#39FF14]">ARC</span>
              </div>
            </div>

            {/* Email (optional) */}
            <Input
              type="email"
              placeholder="Email for token claim instructions (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-5 bg-black/40 border-white/15 text-white placeholder:text-white/30 font-mono text-sm"
              data-testid="presale-email-input"
            />

            {/* Action */}
            <button
              onClick={submit}
              disabled={submitting}
              className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="presale-submit-button"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
              ) : !wallet?.address ? (
                <><Wallet className="w-4 h-4" /> Connect Wallet to Buy</>
              ) : done ? (
                <><CheckCircle2 className="w-4 h-4" /> Allocation Reserved · Buy More</>
              ) : (
                <>Buy ARC Tokens</>
              )}
            </button>

            <p className="mt-3 font-mono text-[10px] text-white/40 text-center">
              UI uses MetaMask account. Final token transfer executes on-chain after presale ends.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
